/* eslint-disable react-hooks/exhaustive-deps */
import React, { FormEvent, useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Form, InputGroup, FormControl, Button } from 'react-bootstrap'
import Participant from "../../library/Participant"
import Theme from "../../library/Theme"
import 'react-phone-number-input/style.css'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'

interface SplashFormProps {
  theme: Theme
  currentParticipant: Participant
  emailOn?: boolean
  firstNameOn?: boolean
  lastNameOn?: boolean
  phoneNumberOn?: boolean
  buttonTitle: string
  handleSuccess: (participant: Participant) => void
}

export default function SplashForm({ theme, currentParticipant, emailOn, firstNameOn, lastNameOn, phoneNumberOn, buttonTitle, handleSuccess }: SplashFormProps) {
  const { t } = useTranslation()

  const [email, setEmail] = useState<string | null>(currentParticipant.email)
  const [firstName, setFirstName] = useState<string | null>(currentParticipant.firstName)
  const [lastName, setLastName] = useState<string | null>(currentParticipant.lastName)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(currentParticipant.phoneNumber)
  const [phoneNumberCountry, setPhoneNumberCountry] = useState<string>("US")

  const [emailValid, setEmailValid] = useState<boolean>(false)
  const [firstNameValid, setFirstNameValid] = useState<boolean>(false)
  const [lastNameValid, setLastNameValid] = useState<boolean>(false)
  const [phoneNumberValid, setPhoneNumberValid] = useState<boolean>(false)

  const [formValid, setFormValid] = useState<boolean | null>(null)

  useEffect(() => {
    let initialEmailValid = email ? Participant.isValidEmail(email) : false
    let initialFirstNameValid = firstName ? Participant.isValidName(firstName) : false
    let initialLastNameValid = lastName ? Participant.isValidName(lastName) : false
    let initialPhoneNumberValid = phoneNumber ? isValidPhoneNumber(phoneNumber) : false

    setEmailValid(initialEmailValid)
    setFirstNameValid(initialFirstNameValid)
    setLastNameValid(initialLastNameValid)
    setPhoneNumberValid(initialPhoneNumberValid)

    checkFormValid(initialEmailValid, initialFirstNameValid, initialLastNameValid, initialPhoneNumberValid)
  }, [])

  const validateEmail = (value: string) => {
    let formattedEmail = String(value).toLowerCase()
    setEmail(formattedEmail)

    let isValid = Participant.isValidEmail(formattedEmail)
    setEmailValid(isValid)
    checkFormValid(isValid, firstNameValid, lastNameValid, phoneNumberValid)
  }

  const validateFirstName = (value: string) => {
    setFirstName(value)

    let isValid = Participant.isValidName(value)
    setFirstNameValid(isValid)

    checkFormValid(emailValid, isValid, lastNameValid, phoneNumberValid)
  }

  const validateLastName = (value: string) => {
    setLastName(value)

    let isValid = Participant.isValidName(value)
    setLastNameValid(isValid)

    checkFormValid(emailValid, firstNameValid, isValid, phoneNumberValid)
  }

  const validatePhoneNumber = (value?: string) => {
    if (!value) {
      return
    }
    setPhoneNumber(value)

    let isValid = isValidPhoneNumber(value)
    setPhoneNumberValid(isValid)

    checkFormValid(emailValid, firstNameValid, lastNameValid, isValid)
  }

  const checkFormValid = (emailValid: boolean, firstNameValid: boolean, lastNameValid: boolean, phoneNumberValid: boolean) => {
    let passEmail = emailValid || !emailOn
    let passFirstName = firstNameValid || !firstNameOn
    let passLastName = lastNameValid || !lastNameOn
    let passPhoneNumber = phoneNumberValid || !phoneNumberOn

    setFormValid(
      passEmail
      && passFirstName
      && passLastName
      && passPhoneNumber
    )
  }

  const submit = (e?: FormEvent) => {
    e?.preventDefault()

    if (!formValid) return

    let participant = currentParticipant
    if (email) {
      participant.email = email
    }
    if (firstName) {
      participant.firstName = firstName
    }
    if (lastName) {
      participant.lastName = lastName
    }
    if (phoneNumber) {
      participant.phoneNumber = phoneNumber
    }

    participant.acceptedTerms = new Date()
    handleSuccess(participant)
  }

  const showFirstName = firstNameOn
  const showLastName = lastNameOn
  const showPhoneNumber = phoneNumberOn
  const showEmail = emailOn

  const canEditFirstName = !currentParticipant.firstName || currentParticipant.firstName == null || Participant.isValidName(currentParticipant.firstName)
  const canEditLastName = !currentParticipant.lastName || currentParticipant.lastName == null || Participant.isValidName(currentParticipant.lastName)
  const canEditPhoneNumber = !currentParticipant.phoneNumber || !isValidPhoneNumber(currentParticipant.phoneNumber)
  const canEditEmail = !currentParticipant.email || !Participant.isValidEmail(String(currentParticipant.email).toLowerCase())

  const showForm = showEmail || showFirstName || showLastName || showPhoneNumber
  if (!showForm) {
    return (
      <button className="bigButton" style={theme.bigButtonStyle} onClick={submit}>
        {buttonTitle}
      </button>
    )
  }

  const emailOnly = emailOn && !firstNameOn && !lastNameOn && !phoneNumberOn
  if (emailOnly && !currentParticipant.email) {
    return (
      <Form>
        <InputGroup>
          <FormControl
            type="email"
            name="email"
            defaultValue={email ?? ""}
            className="soloEmail"
            placeholder={t("splash.emailPlaceholder")}
            aria-label="Email"
            onChange={(e) => validateEmail(e.target.value)}
            required
            disabled={!canEditEmail}
            autoComplete="email"
          />
          <InputGroup.Append>
            <Button
              type="submit"
              style={(theme.mode === "light" ? theme.buttonStyle : theme.inverseButtonStyle) as any}
              onClick={submit}
              disabled={!formValid}
            >
              {t("splash.continue")}
            </Button>
          </InputGroup.Append>
        </InputGroup>
      </Form>
    )
  }

  // One or more data capture fields besides email
  return (
    <Form>
      {(showFirstName || showLastName) &&
        <div className="twoColumnFlex">
          {showFirstName &&
            <div>
              <Form.Label style={{ color: theme.textColor }}>{t("splash.firstName")}</Form.Label>
              <FormControl
                type="text"
                name="firstName"
                defaultValue={firstName ?? ""}
                placeholder={t("splash.firstNamePlaceholder")}
                aria-label="Last name"
                onChange={(e) => validateFirstName(e.target.value ?? "")}
                required
                disabled={!canEditFirstName}
                autoComplete="given-name"
              />
            </div>
          }
          {showLastName &&
            <div>
              <Form.Label style={{ color: theme.textColor }}>{t("splash.lastName")}</Form.Label>
              <FormControl
                type="text"
                name="lastName"
                defaultValue={lastName ?? ""}
                placeholder={t("splash.lastNamePlaceholder")}
                aria-label="First name"
                onChange={(e) => validateLastName(e.target.value ?? "")}
                required
                disabled={!canEditLastName}
                autoComplete="family-name"
              />
            </div>
          }
        </div>
      }

      {showEmail &&
        <>
          <Form.Label style={{ color: theme.textColor }} className="formMargin">{t("splash.emailAddress")}</Form.Label>
          <FormControl
            type="email"
            name="email"
            defaultValue={email ?? ""}
            placeholder={t("splash.emailPlaceholderAlternate")}
            aria-label="Email"
            onChange={(e) => validateEmail(e.target.value)}
            required
            disabled={!canEditEmail}
            autoComplete="email"
          />
        </>
      }

      {showPhoneNumber &&
        <>
          <Form.Label style={{ color: theme.textColor }} className="formMargin">{t("splash.phoneNumber")}</Form.Label>
          <PhoneInput
            country={phoneNumberCountry}
            placeholder={phoneNumberCountry === "US" ? t("splash.phoneNumberPlaceholder.US") : ""}
            value={phoneNumber ?? ""}
            disabled={!canEditPhoneNumber}
            onChange={validatePhoneNumber}
            onCountryChange={(countryCode) => setPhoneNumberCountry(countryCode ?? "US")}
          />
        </>
      }

      <Button
        type="submit"
        className={`fullWidth ${formValid && "enabled"}`}
        style={(theme.mode === "light" ? theme.buttonStyle : theme.inverseButtonStyle) as any}
        onClick={submit}
        disabled={!formValid}
      >
        {t("splash.continue")}
      </Button>
    </Form>
  )
}
